import React, { useState, useEffect } from 'react';
import { db, database } from '../../../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  arrayRemove,
  deleteField,
} from 'firebase/firestore';
import { getDatabase, ref, get, remove } from 'firebase/database';
import StudentList from './StudentList';
import SessionList from './SessionList';
import Stats from './Stats';
import ManageSession from './ManageSession';
import Preloader from '../../Preloader';

const ClassDetails = ({ selectedClass, closeClass, theme }) => {
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');

  const fetchClassDetails = async () => {
    setLoading(true);
    try {
      // Start fetching all data in parallel
      const enrollmentsPromise = getDoc(doc(db, 'enrollments', selectedClass.id));
      const gradesPromise = getDoc(doc(db, 'grades', selectedClass.id));
      const dbRealtime = getDatabase();
      const sessionsRef = ref(dbRealtime, `sessions/${selectedClass.id}`);
      const sessionsPromise = get(sessionsRef);

      const [enrollmentsDoc, gradesDoc, sessionsSnapshot] = await Promise.all([
        enrollmentsPromise,
        gradesPromise,
        sessionsPromise,
      ]);

      // Process students
      if (enrollmentsDoc.exists()) {
        const studentUIDs = enrollmentsDoc.data().students || [];
        const studentPromises = studentUIDs.map(async (uid) => {
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists()) {
            return { uid, ...userDoc.data() };
          }
          return null;
        });
        const studentsData = await Promise.all(studentPromises);
        setStudents(studentsData.filter((s) => s !== null));
      } else {
        setStudents([]);
      }

      // Process grades
      if (gradesDoc.exists()) {
        setGrades(gradesDoc.data());
      } else {
        setGrades({});
      }

      // Process sessions
      if (sessionsSnapshot.exists()) {
        const sessionsData = sessionsSnapshot.val();
        setSessions(
          Object.entries(sessionsData).map(([id, data]) => ({ id, ...data }))
        );
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Error fetching class details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeStudent = async (studentUid) => {
    if (!window.confirm('Are you sure you want to remove this student?')) {
      return;
    }
  
    setLoading(true);
  
    try {
      // Remove student from enrollments
      await updateDoc(doc(db, 'enrollments', selectedClass.id), {
        students: arrayRemove(studentUid),
      });
  
      // Remove student's grades
      await updateDoc(doc(db, 'grades', selectedClass.id), {
        [`students.${studentUid}`]: deleteField(),
      });
  
      // Function to delete student's answers from Realtime Database
      const deleteStudentAnswers = async () => {
        const answersRef = ref(database, 'answers');
        const classesSnapshot = await get(answersRef);
  
        if (!classesSnapshot.exists()) {
          console.log('No classes found in answers.');
          return;
        }
  
        const classes = classesSnapshot.val();
  
        // Iterate over each class
        for (const classId in classes) {
          const sessions = classes[classId];
  
          // Iterate over each session in the class
          for (const sessionId in sessions) {
            const questions = sessions[sessionId];
  
            // Iterate over each question in the session
            for (const questionNumber in questions) {
              const studentAnswers = questions[questionNumber];
  
              // Check if the student's answer exists for this question
              if (studentAnswers && studentAnswers.hasOwnProperty(studentUid)) {
                const answerRef = ref(
                  database,
                  `answers/${classId}/${sessionId}/${questionNumber}/${studentUid}`
                );
  
                // Remove the student's answer
                await remove(answerRef);
                console.log(
                  `Removed answer for student ${studentUid} from Class ${classId}, Session ${sessionId}, Question ${questionNumber}`
                );
              }
            }
          }
        }
      };
  
      // Call the function to delete student's answers
      await deleteStudentAnswers();
  
      // Refresh students and grades
      await fetchClassDetails();
    } catch (error) {
      console.error('Error removing student:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <div
      className="w-full max-w-none min-w-0 mt-4 px-3 sm:px-4 py-4 rounded overflow-x-visible"
      style={{ backgroundColor: 'var(--section-bg)', color: 'var(--text-color)' }}
    >
      <div className="flex justify-between items-center gap-3 mb-4 min-w-0">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-color)' }}>
          {selectedClass.class_name}
        </h2>
        <button
          onClick={closeClass}
          className="font-bold py-2 px-4 rounded transition-all duration-200"
          style={{ backgroundColor: 'var(--primary-color)', color: 'var(--nav-active-text)' }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
          }
        >
          &times;
        </button>
      </div>

      {/* Nav Tabs */}
      <ul
        className="ce-class-tabs grid grid-cols-2 sm:flex sm:flex-wrap w-full min-w-0 border-b mb-3"
        role="tablist"
        style={{ borderColor: 'var(--muted-color)' }}
      >
        {['stats', 'students', 'sessions', 'manageSession'].map((tab) => (
          <li key={tab} className="min-w-0">
            <button
              type="button"
              className="block w-full cursor-pointer py-2 px-3 sm:px-4 text-center transition-colors duration-200"
              role="tab"
              onClick={() => setActiveTab(tab)}
              style={
                activeTab === tab
                  ? {
                      color: 'var(--primary-color)',
                      borderBottom: '2px solid var(--primary-color)',
                    }
                  : { color: 'var(--muted-color)' }
              }
              onMouseOver={(e) => {
                if (activeTab !== tab) {
                  e.currentTarget.style.color = 'var(--text-color)';
                }
              }}
              onMouseOut={(e) => {
                if (activeTab !== tab) {
                  e.currentTarget.style.color = 'var(--muted-color)';
                }
              }}
            >
              {tab === 'manageSession'
                ? 'Manage Session'
                : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-3 w-full min-w-0">
        {activeTab === 'stats' && (
          <div className="block w-full min-w-0" role="tabpanel">
            <Stats
              students={students}
              sessions={sessions}
              grades={grades}
              classId={selectedClass.id}
              theme={theme}
            />
          </div>
        )}
        {activeTab === 'students' && (
          <div className="block w-full min-w-0" role="tabpanel">
            <StudentList
              students={students}
              grades={grades}
              removeStudent={removeStudent}
              selectedStudent={selectedStudent}
              setSelectedStudent={setSelectedStudent}
              sessions={sessions}
              classId={selectedClass.id}
              theme={theme}
            />
          </div>
        )}
        {activeTab === 'sessions' && (
          <div className="block w-full min-w-0" role="tabpanel">
            <SessionList
              sessions={sessions}
              classId={selectedClass.id}
              fetchClassDetails={fetchClassDetails}
              theme={theme}
            />
          </div>
        )}
        {activeTab === 'manageSession' && (
          <div className="block w-full min-w-0" role="tabpanel">
            <ManageSession
              classId={selectedClass.id}
              courseId={selectedClass.id}
              theme={theme}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassDetails;
